import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  CircularProgress,
  Stack,
  Paper,
  Toolbar,
  AppBar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Close as CloseIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  CheckCircle as ApproveIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircleOutline as QaPassIcon,
  ErrorOutline as QaFailIcon,
  WarningAmber as QaWarnIcon
} from '@mui/icons-material';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { configureCKEditorUpload } from './ckEditorUploadAdapter';
import api from '../api/axios';
import LearnerReadingBody from './LearnerReadingBody';

// Lazy load ClassicEditor to avoid conflicts with DecoupledEditor
let ClassicEditor = null;
const loadClassicEditor = async () => {
  if (!ClassicEditor) {
    const module = await import('@ckeditor/ckeditor5-build-classic');
    ClassicEditor = module.default;
  }
  return ClassicEditor;
};

export default function BokPageViewerModal({ 
  open, 
  onClose, 
  subtaskCode, 
  qualificationId,
  title = 'BOK Pages'
}) {
  const [pages, setPages] = useState([]);
  const [document, setDocument] = useState(null);
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [compiling, setCompiling] = useState(false);
  const [classicEditorLoaded, setClassicEditorLoaded] = useState(false);
  const [referencesAppendixHtml, setReferencesAppendixHtml] = useState('');
  const [referencesPendingCompletion, setReferencesPendingCompletion] = useState(false);
  const [referencesDesignerNotice, setReferencesDesignerNotice] = useState(null);
  const [qaStatus, setQaStatus] = useState(null);
  const [qaCompileAllowed, setQaCompileAllowed] = useState(true);
  const [qaCompileMessage, setQaCompileMessage] = useState(null);
  const [qaEvaluation, setQaEvaluation] = useState(null);
  const [qaManualNotes, setQaManualNotes] = useState('');
  const [qaAuditLog, setQaAuditLog] = useState([]);
  const [qaNeedsEval, setQaNeedsEval] = useState(false);
  const [qaLoading, setQaLoading] = useState(false);
  const [qaSavingNotes, setQaSavingNotes] = useState(false);
  const [adminForce, setAdminForce] = useState(false);
  const [forceReason, setForceReason] = useState('');
  const [jwtRole, setJwtRole] = useState(null);

  const qaStatusLabel = (s) => {
    const map = {
      draft: 'Draft',
      generated: 'Generated',
      qa_review_required: 'QA review required',
      qa_failed: 'QA failed',
      qa_passed: 'QA passed',
      approved_for_deployment: 'Approved for deployment'
    };
    return map[s] || s || '—';
  };

  // Load ClassicEditor when modal opens
  useEffect(() => {
    if (open) {
      loadClassicEditor().then(() => {
        setClassicEditorLoaded(true);
      });
      try {
        const t = localStorage.getItem('token');
        if (t) {
          const parts = t.split('.');
          if (parts[1]) {
            const json = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
            setJwtRole(json.role || null);
          }
        }
      } catch {
        setJwtRole(null);
      }
    }
  }, [open]);

  // Fetch pages when modal opens
  useEffect(() => {
    if (open && subtaskCode && qualificationId) {
      fetchPages();
    }
  }, [open, subtaskCode, qualificationId]);

  // Update edited content when active page changes
  useEffect(() => {
    if (pages.length > 0 && activePageIndex < pages.length) {
      setEditedContent(pages[activePageIndex]?.page_content || '');
    }
  }, [activePageIndex, pages]);

  const fetchPages = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(
        `/qualifications/${qualificationId}/learning-content/bok-pages/${subtaskCode}`
      );
      
      if (response.data.success) {
        setPages(response.data.pages || []);
        setDocument(response.data.document);
        setReferencesAppendixHtml(response.data.references_appendix_html || '');
        setReferencesPendingCompletion(Boolean(response.data.references_pending_completion));
        setReferencesDesignerNotice(response.data.references_designer_notice || null);
        setQaStatus(response.data.qa_status || null);
        setQaCompileAllowed(response.data.qa_compile_allowed !== false);
        setQaCompileMessage(response.data.qa_compile_message || null);

        // Reset to first page if current index is out of bounds
        if (activePageIndex >= (response.data.pages || []).length) {
          setActivePageIndex(0);
        }
        try {
          const qaRes = await api.get(
            `/qualifications/${qualificationId}/learning-content/bok-documents/${encodeURIComponent(subtaskCode)}/qa`
          );
          if (qaRes.data.success) {
            setQaEvaluation(qaRes.data.evaluation || null);
            setQaManualNotes(qaRes.data.manual_notes || '');
            setQaAuditLog(qaRes.data.audit_log || []);
            setQaNeedsEval(Boolean(qaRes.data.needs_evaluation));
          }
        } catch {
          /* QA endpoint optional */
        }
      } else {
        setError('Failed to fetch pages');
      }
    } catch (err) {

      setError(err.response?.data?.message || 'Failed to fetch pages');
    } finally {
      setLoading(false);
    }
  };

  const handleRunQaEvaluate = async () => {
    try {
      setQaLoading(true);
      setError(null);
      const r = await api.post(
        `/qualifications/${qualificationId}/learning-content/bok-documents/${encodeURIComponent(subtaskCode)}/qa/evaluate`
      );
      if (r.data.success) {
        setQaEvaluation(r.data.evaluation);
        setQaStatus(r.data.qa_status);
        setQaNeedsEval(false);
        setQaAuditLog(r.data.audit_log || []);
        setQaCompileAllowed(true);
        setQaCompileMessage(null);
        const pagesRes = await api.get(
          `/qualifications/${qualificationId}/learning-content/bok-pages/${subtaskCode}`
        );
        if (pagesRes.data.success) {
          setQaCompileAllowed(pagesRes.data.qa_compile_allowed !== false);
          setQaCompileMessage(pagesRes.data.qa_compile_message || null);
          setQaStatus(pagesRes.data.qa_status || r.data.qa_status);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'QA evaluation failed');
    } finally {
      setQaLoading(false);
    }
  };

  const handleSaveQaNotes = async () => {
    try {
      setQaSavingNotes(true);
      await api.post(
        `/qualifications/${qualificationId}/learning-content/bok-documents/${encodeURIComponent(subtaskCode)}/qa/notes`,
        { notes: qaManualNotes }
      );
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save QA notes');
    } finally {
      setQaSavingNotes(false);
    }
  };

  const handleApproveDeployment = async () => {
    try {
      setQaLoading(true);
      setError(null);
      await api.post(
        `/qualifications/${qualificationId}/learning-content/bok-documents/${encodeURIComponent(subtaskCode)}/qa/approve-deployment`,
        {
          notes: qaManualNotes,
          force: adminForce && jwtRole === 'admin',
          force_reason: adminForce ? forceReason : undefined
        }
      );
      setQaStatus('approved_for_deployment');
      setQaCompileAllowed(true);
      setQaCompileMessage(null);
      setAdminForce(false);
      setForceReason('');
      await fetchPages();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not approve for deployment');
    } finally {
      setQaLoading(false);
    }
  };

  const handleRevokeDeployment = async () => {
    try {
      setQaLoading(true);
      await api.post(
        `/qualifications/${qualificationId}/learning-content/bok-documents/${encodeURIComponent(subtaskCode)}/qa/revoke-deployment`
      );
      setQaStatus('qa_review_required');
      await fetchPages();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not revoke deployment approval');
    } finally {
      setQaLoading(false);
    }
  };

  const handleSave = async () => {
    if (!pages[activePageIndex]) return;
    
    try {
      setSaving(true);
      
      await api.put(
        `/qualifications/${qualificationId}/learning-content/bok-pages/${pages[activePageIndex].id}`,
        { content: editedContent }
      );
      
      // Update local state
      setPages(prev => prev.map((page, index) => 
        index === activePageIndex 
          ? { ...page, page_content: editedContent }
          : page
      ));
      
      setEditMode(false);
    } catch (err) {

      setError(err.response?.data?.message || 'Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!pages[activePageIndex]) return;
    
    try {
      setSaving(true);
      
      await api.put(
        `/qualifications/${qualificationId}/learning-content/bok-pages/${pages[activePageIndex].id}/approve`
      );
      
      // Update local state
      setPages(prev => prev.map((page, index) => 
        index === activePageIndex 
          ? { ...page, status: 'approved', approved_at: new Date().toISOString() }
          : page
      ));
      
    } catch (err) {

      setError(err.response?.data?.message || 'Failed to approve page');
    } finally {
      setSaving(false);
    }
  };

  const handleCompile = async () => {
    try {
      setCompiling(true);
      
      const response = await api.post(
        `/qualifications/${qualificationId}/learning-content/bok-documents/${subtaskCode}/compile`
      );
      
      if (response.data.success) {
        // Open download link
        window.open(response.data.download_url, '_blank');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to compile document';
      const code = err.response?.data?.code;
      if (code === 'QA_DEPLOYMENT_BLOCKED') {
        setQaCompileMessage(msg);
        setQaCompileAllowed(false);
      }
      setError(msg);
    } finally {
      setCompiling(false);
    }
  };

  const handlePrevPage = () => {
    if (activePageIndex > 0) {
      setActivePageIndex(activePageIndex - 1);
      setEditMode(false);
    }
  };

  const handleNextPage = () => {
    if (activePageIndex < pages.length - 1) {
      setActivePageIndex(activePageIndex + 1);
      setEditMode(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'completed': return 'primary';
      case 'generating': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <ApproveIcon />;
      case 'completed': return <ViewIcon />;
      case 'generating': return <CircularProgress size={16} />;
      default: return null;
    }
  };

  const allPagesApproved = pages.length > 0 && pages.every(page => page.status === 'approved');
  const currentPage = pages[activePageIndex];

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Typography variant="h6">{title}</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {qaStatus ? (
              <Chip
                size="small"
                label={qaStatusLabel(qaStatus)}
                color={
                  qaStatus === 'approved_for_deployment'
                    ? 'success'
                    : qaStatus === 'qa_passed'
                      ? 'primary'
                      : qaStatus === 'qa_failed'
                        ? 'error'
                        : 'warning'
                }
              />
            ) : null}
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', height: '100%' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" width="100%">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box width="100%" p={2}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : (
          <>
            {/* Left Sidebar - Page List */}
            <Box 
              sx={{ 
                width: 300, 
                borderRight: 1, 
                borderColor: 'divider',
                overflow: 'auto',
                maxHeight: '100%'
              }}
            >
              <Box p={2}>
                <Typography variant="h6" gutterBottom>
                  Pages ({pages.length})
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Approved: {pages.filter(p => p.status === 'approved').length}
                </Typography>
              </Box>
              
              <List dense>
                {pages.map((page, index) => (
                  <ListItem
                    key={page.id}
                    button
                    selected={index === activePageIndex}
                    onClick={() => {
                      setActivePageIndex(index);
                      setEditMode(false);
                    }}
                    sx={{ 
                      borderLeft: index === activePageIndex ? 3 : 0,
                      borderColor: 'primary.main',
                      pl: index === activePageIndex ? 1 : 2
                    }}
                  >
                    <ListItemIcon>
                      {getStatusIcon(page.status)}
                    </ListItemIcon>
                    <ListItemText
                      primary={`Page ${page.page_number}`}
                      secondary={
                        <Box>
                          <Typography variant="body2" noWrap>
                            {page.page_heading || 'Untitled'}
                          </Typography>
                          <Chip 
                            label={page.status} 
                            size="small" 
                            color={getStatusColor(page.status)}
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            {/* Main Content Area */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {currentPage ? (
                <>
                  {/* Toolbar */}
                  <AppBar position="static" color="default" elevation={1}>
                    <Toolbar>
                      <IconButton 
                        onClick={handlePrevPage} 
                        disabled={activePageIndex === 0}
                        color="inherit"
                      >
                        <PrevIcon />
                      </IconButton>
                      
                      <Typography variant="h6" sx={{ flex: 1, mx: 2 }}>
                        Page {currentPage.page_number}: {currentPage.page_heading || 'Untitled'}
                      </Typography>
                      
                      <IconButton 
                        onClick={handleNextPage} 
                        disabled={activePageIndex === pages.length - 1}
                        color="inherit"
                      >
                        <NextIcon />
                      </IconButton>
                      
                      <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                      
                      {editMode ? (
                        <>
                          <Button
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={saving}
                            color="primary"
                            sx={{ mr: 1 }}
                          >
                            {saving ? 'Saving...' : 'Save'}
                          </Button>
                          <Button
                            onClick={() => setEditMode(false)}
                            color="inherit"
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            startIcon={<EditIcon />}
                            onClick={() => setEditMode(true)}
                            color="primary"
                            sx={{ mr: 1 }}
                          >
                            Edit
                          </Button>
                          {currentPage.status !== 'approved' && (
                            <Button
                              startIcon={<ApproveIcon />}
                              onClick={handleApprove}
                              disabled={saving}
                              color="success"
                            >
                              {saving ? 'Approving...' : 'Approve'}
                            </Button>
                          )}
                        </>
                      )}
                    </Toolbar>
                  </AppBar>

                  {/* Content Area */}
                  <Box
                    className="learner-reading-host"
                    sx={{ flex: 1, p: 2, overflow: 'auto', minHeight: 0 }}
                  >
                    {editMode ? (
                      classicEditorLoaded && ClassicEditor ? (
                        <CKEditor
                          editor={ClassicEditor}
                          data={editedContent}
                          onChange={(event, editor) => {
                            setEditedContent(editor.getData());
                          }}
                          config={{
                            toolbar: [
                              'heading', '|',
                              'bold', 'italic', '|',
                              'bulletedList', 'numberedList', '|',
                              'outdent', 'indent', '|',
                              'blockQuote', 'insertTable', '|',
                              'undo', 'redo'
                            ]
                          }}
                          onReady={(editor) => configureCKEditorUpload(editor)}
                        />
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                          <Typography>Loading editor...</Typography>
                        </Box>
                      )
                    ) : (
                      <Paper sx={{ p: 3, minHeight: 400, bgcolor: '#fff' }}>
                        <LearnerReadingBody
                          aria-label={
                            currentPage.page_heading
                              ? `${currentPage.page_heading}, page ${currentPage.page_number}`
                              : `Page ${currentPage.page_number}`
                          }
                        >
                          <div
                            dangerouslySetInnerHTML={{
                              __html: currentPage.page_content || 'No content available'
                            }}
                          />
                        </LearnerReadingBody>
                      </Paper>
                    )}
                  </Box>

                  {referencesAppendixHtml ? (
                    <Box
                      sx={{
                        flexShrink: 0,
                        borderTop: 1,
                        borderColor: 'divider',
                        p: 2,
                        bgcolor: 'grey.50',
                        maxHeight: '36%',
                        overflow: 'auto'
                      }}
                    >
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        References (document-level, matches learner PDF/export)
                      </Typography>
                      {referencesPendingCompletion ? (
                        <Alert severity="warning" sx={{ mb: 1 }}>
                          {referencesDesignerNotice ||
                            'Verify references against real sources. Complete placeholders and expand retrieval URLs where needed.'}
                        </Alert>
                      ) : null}
                      <LearnerReadingBody className="learner-reading--compact" aria-label="References">
                        <div dangerouslySetInnerHTML={{ __html: referencesAppendixHtml }} />
                      </LearnerReadingBody>
                    </Box>
                  ) : null}
                </>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography variant="h6" color="text.secondary">
                    No pages available
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
        <Accordion defaultExpanded sx={{ mx: 1, mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>Quality assurance (learner reading)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {qaNeedsEval && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Run automated QA to record pass/fail checks and unlock deployment when requirements are met.
              </Alert>
            )}
            {qaCompileMessage && !qaCompileAllowed && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {qaCompileMessage}
              </Alert>
            )}
            {qaEvaluation && (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Summary:</strong> {qaEvaluation.summary}
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  Automated checklist
                </Typography>
                <List dense disablePadding sx={{ mb: 1 }}>
                  {Object.entries(qaEvaluation.checklist || {}).map(([key, row]) => (
                    <ListItem key={key} disableGutters>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {row.pass ? (
                          <QaPassIcon color="success" fontSize="small" />
                        ) : row.severity === 'critical' ? (
                          <QaFailIcon color="error" fontSize="small" />
                        ) : (
                          <QaWarnIcon color="warning" fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={key.replace(/_/g, ' ')}
                        secondary={row.message}
                        primaryTypographyProps={{ variant: 'body2', textTransform: 'capitalize' }}
                      />
                    </ListItem>
                  ))}
                </List>
                <Typography variant="subtitle2" gutterBottom>
                  Issues ({(qaEvaluation.issues || []).length})
                </Typography>
                <List dense disablePadding sx={{ mb: 1, maxHeight: 200, overflow: 'auto' }}>
                  {(qaEvaluation.issues || []).map((issue) => (
                    <ListItem key={issue.id} disableGutters alignItems="flex-start">
                      <ListItemText
                        primary={`[${issue.severity}] ${issue.code}`}
                        secondary={issue.message}
                        primaryTypographyProps={{ variant: 'caption', fontWeight: 600 }}
                        secondaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleRunQaEvaluate}
                disabled={qaLoading || !subtaskCode}
              >
                {qaLoading ? 'Running…' : 'Run automated QA'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleApproveDeployment}
                disabled={qaLoading || qaStatus === 'approved_for_deployment'}
                color="success"
              >
                Approve for deployment
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleRevokeDeployment}
                disabled={qaLoading || qaStatus !== 'approved_for_deployment'}
                color="warning"
              >
                Revoke deployment approval
              </Button>
            </Stack>
            {jwtRole === 'admin' && (
              <Box sx={{ mb: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={adminForce}
                      onChange={(e) => setAdminForce(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Admin: force deployment approval (QA failed)"
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Override reason (min 10 characters)"
                  value={forceReason}
                  onChange={(e) => setForceReason(e.target.value)}
                  disabled={!adminForce}
                  sx={{ mt: 1 }}
                />
              </Box>
            )}
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Manual QA notes (saved to audit trail)"
              value={qaManualNotes}
              onChange={(e) => setQaManualNotes(e.target.value)}
              sx={{ mb: 1 }}
            />
            <Button size="small" onClick={handleSaveQaNotes} disabled={qaSavingNotes}>
              {qaSavingNotes ? 'Saving…' : 'Save notes'}
            </Button>
            {qaAuditLog.length > 0 && (
              <>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                  Recent QA audit ({Math.min(qaAuditLog.length, 8)} of {qaAuditLog.length})
                </Typography>
                <List dense disablePadding sx={{ maxHeight: 160, overflow: 'auto' }}>
                  {qaAuditLog.slice(-8).reverse().map((entry, idx) => (
                    <ListItem key={`${entry.ts}-${idx}`} disableGutters>
                      <ListItemText
                        primary={`${entry.action || 'event'} — ${entry.ts || ''}`}
                        secondary={entry.notes || entry.email || ''}
                        primaryTypographyProps={{ variant: 'caption' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </AccordionDetails>
        </Accordion>
        <Box display="flex" justifyContent="space-between" width="100%" p={2} pt={0}>
          <Box>
            {pages.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                {pages.filter(p => p.status === 'approved').length} of {pages.length} pages approved
              </Typography>
            )}
          </Box>

          <Box>
            <Button onClick={onClose} sx={{ mr: 1 }}>
              Close
            </Button>
            {allPagesApproved && (
              <Button
                variant="contained"
                startIcon={compiling ? <CircularProgress size={16} /> : <DownloadIcon />}
                onClick={handleCompile}
                disabled={compiling || qaCompileAllowed === false}
                color="primary"
                title={qaCompileAllowed === false ? qaCompileMessage || 'QA gate' : ''}
              >
                {compiling ? 'Compiling...' : 'Compile document'}
              </Button>
            )}
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
